import { useEffect, useMemo, useState } from "react";
import {
  fetchCredential,
  testCredential,
  useCredentialKinds,
  useCredentials,
  useDeleteCredential,
  useMe,
  useSaveCredential,
} from "../../api/hooks";
import type { CredentialInfo, FieldSpec } from "../../api/types";
import { useSession } from "../../app/store";
import { Modal, Spinner, errText, fmtDate } from "../../components/ui";
import "./platform.css";

const MASK = "••••••";

/**
 * Per-tenant secret vault. Secrets are write-only: the API returns a mask for stored secret
 * fields; leaving the mask untouched on save keeps the stored value.
 */
export function CredentialsDialog({ onClose, initialKind, onCreated }: { onClose: () => void; initialKind?: string; onCreated?: (id: string) => void }) {
  const me = useMe();
  const creds = useCredentials();
  const kinds = useCredentialKinds();
  const [selected, setSelected] = useState<string | "new" | null>(initialKind ? "new" : null);
  const canEdit = !!me.data && (me.data.is_admin || me.data.roles.some((r) => r.toLowerCase() === "operator admin"));

  useEffect(() => {
    if (selected === null && creds.data?.length) setSelected(creds.data[0].id);
  }, [creds.data, selected]);

  return (
    <Modal title={`Credentials · ${me.data?.tenant ?? ""}`} onClose={onClose} width={860}>
      <div className="cred-dialog">
        <div className="cred-list">
          <div className="row" style={{ padding: "4px 8px" }}>
            <span className="dim small">{creds.data?.length ?? 0} stored</span>
            <span className="spacer" />
            {canEdit ? (
              <button className="btn sm" onClick={() => setSelected("new")}>
                + New
              </button>
            ) : null}
          </div>
          <div className="list">
            {(creds.data ?? []).map((c) => (
              <div key={c.id} className={`list-item${selected === c.id ? " sel" : ""}`} onClick={() => setSelected(c.id)}>
                <span className="badge">{kinds.data?.kinds[c.kind]?.label ?? c.kind}</span>
                <div className="name">
                  <div>{c.name}</div>
                  <div className="small dim mono">{c.id}</div>
                </div>
              </div>
            ))}
            {creds.isLoading ? <Spinner label="loading" /> : null}
            {!creds.isLoading && !creds.data?.length ? (
              <div className="dim small" style={{ padding: 10 }}>
                No credentials yet. Sinks that need a login (email, SFTP, FTP, webhook token) reference one by id.
              </div>
            ) : null}
          </div>
        </div>
        <div className="cred-form">
          {selected === "new" ? (
            <CredentialForm key="new" create initialKind={initialKind} canEdit={canEdit} onDone={(id) => (setSelected(id), onCreated?.(id))} />
          ) : selected ? (
            <CredentialForm key={selected} id={selected} canEdit={canEdit} onDone={() => undefined} onDeleted={() => setSelected(null)} />
          ) : (
            <div className="empty">
              <h3>Select or create a credential</h3>
              Secrets are encrypted at rest and never returned by the API.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function CredentialForm({
  id,
  create,
  initialKind,
  canEdit,
  onDone,
  onDeleted,
}: {
  id?: string;
  create?: boolean;
  initialKind?: string;
  canEdit: boolean;
  onDone: (id: string) => void;
  onDeleted?: () => void;
}) {
  const kinds = useCredentialKinds();
  const save = useSaveCredential();
  const del = useDeleteCredential();
  const toast = useSession((s) => s.toast);
  const [loaded, setLoaded] = useState<CredentialInfo | null>(null);
  const [credId, setCredId] = useState(id ?? "");
  const [name, setName] = useState("");
  const [kind, setKind] = useState(initialKind ?? "smtp");
  const [fields, setFields] = useState<Record<string, unknown>>({});
  const [testing, setTesting] = useState<{ ok: boolean; detail: string } | "busy" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetchCredential(id)
      .then((c) => {
        setLoaded(c);
        setName(c.name);
        setKind(c.kind);
        setFields(c.fields);
      })
      .catch((e) => setError(errText(e)));
  }, [id]);

  const spec = kinds.data?.kinds[kind];
  const fieldSpecs: FieldSpec[] = useMemo(() => spec?.fields ?? [], [spec]);

  useEffect(() => {
    if (!create) return;
    // reset field values to defaults when the kind changes
    const next: Record<string, unknown> = {};
    for (const f of fieldSpecs) if (f.default !== undefined) next[f.name] = f.default;
    setFields(next);
  }, [create, fieldSpecs]);

  const submit = async () => {
    setError(null);
    const cid = (credId || name).trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, "_").replace(/^_+|_+$/g, "");
    if (!name.trim()) return setError("name is required");
    const missing = fieldSpecs.filter((f) => f.required && (fields[f.name] === undefined || fields[f.name] === ""));
    if (missing.length) return setError(`required: ${missing.map((m) => m.label).join(", ")}`);
    try {
      const res = await save.mutateAsync({ id: create ? cid : id!, create: !!create, name: name.trim(), kind, fields });
      toast(`credential ${res.id} saved`, "success");
      setLoaded(res);
      setFields(res.fields);
      onDone(res.id);
    } catch (e) {
      setError(errText(e));
    }
  };

  const test = async () => {
    if (!id) return;
    setTesting("busy");
    try {
      const r = await testCredential(id);
      setTesting({ ok: r.ok, detail: r.detail });
    } catch (e) {
      setTesting({ ok: false, detail: errText(e) });
    }
  };

  const remove = async () => {
    if (!id || !confirm(`Delete credential ${id}? Sinks that reference it will fail to deliver.`)) return;
    await del.mutateAsync(id);
    toast(`credential ${id} deleted`);
    onDeleted?.();
  };

  return (
    <div className="col" style={{ gap: 10 }}>
      <div className="row wrap">
        <label className="field" style={{ flex: 2 }}>
          <span>Name</span>
          <input className="inp" value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} placeholder="Ops mail server" />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span>Id (referenced by sinks)</span>
          <input
            className="inp mono"
            value={create ? credId : id}
            onChange={(e) => setCredId(e.target.value)}
            disabled={!create || !canEdit}
            placeholder={name ? name.trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, "_") : "smtp_main"}
          />
        </label>
        <label className="field">
          <span>Kind</span>
          <select className="inp" value={kind} onChange={(e) => setKind(e.target.value)} disabled={!create || !canEdit}>
            {Object.entries(kinds.data?.kinds ?? {}).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="cred-fields">
        {fieldSpecs.map((f) => (
          <FieldInput key={f.name} spec={f} value={fields[f.name]} disabled={!canEdit} onChange={(v) => setFields({ ...fields, [f.name]: v })} />
        ))}
      </div>
      {loaded ? (
        <div className="dim small">
          updated {fmtDate(loaded.updated_at)} {loaded.created_by ? `· by ${loaded.created_by}` : ""} · masked secrets keep their stored value when you save
        </div>
      ) : null}
      {error ? <div className="problem err">{error}</div> : null}
      {testing && testing !== "busy" ? <div className={`problem ${testing.ok ? "ok" : "err"}`}>{testing.detail}</div> : null}
      <div className="row">
        {!create && id ? (
          <button className="btn sm" onClick={test} disabled={testing === "busy"}>
            {testing === "busy" ? <Spinner /> : "Test connection"}
          </button>
        ) : null}
        <span className="spacer" />
        {!create && canEdit ? (
          <button className="btn sm danger" onClick={remove} disabled={del.isPending}>
            Delete
          </button>
        ) : null}
        {canEdit ? (
          <button className="btn sm primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? <Spinner /> : create ? "Create" : "Save"}
          </button>
        ) : (
          <span className="dim small">operator administrator role required to edit</span>
        )}
      </div>
    </div>
  );
}

export function FieldInput({ spec, value, onChange, disabled }: { spec: FieldSpec; value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  const [reveal, setReveal] = useState(false);
  if (spec.type === "bool") {
    return (
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={value === undefined ? !!spec.default : !!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        {spec.label}
      </label>
    );
  }
  const isMasked = spec.secret && value === MASK;
  return (
    <label className="field">
      <span>
        {spec.label}
        {spec.required ? " *" : ""}
        {spec.secret ? <span className="faint"> · secret</span> : null}
      </span>
      {spec.multiline ? (
        <textarea className="inp mono" rows={3} value={(value as string) ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <span className="row" style={{ gap: 4 }}>
          <input
            className={`inp${spec.secret ? " mono" : ""}`}
            style={{ flex: 1 }}
            type={spec.type === "number" ? "number" : spec.secret && !reveal && !isMasked ? "password" : "text"}
            value={value === undefined || value === null ? "" : String(value)}
            placeholder={spec.default !== undefined ? String(spec.default) : isMasked ? "stored" : ""}
            disabled={disabled}
            onFocus={() => isMasked && onChange("")}
            onChange={(e) => onChange(spec.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
            autoComplete="off"
          />
          {spec.secret && !isMasked ? (
            <button type="button" className="btn ghost sm" onClick={() => setReveal(!reveal)} title={reveal ? "hide" : "show"}>
              {reveal ? "🙈" : "👁"}
            </button>
          ) : null}
        </span>
      )}
    </label>
  );
}
